/**
 * Audit Log Service (US-16-GR-23)
 *
 * Client for the Receipt audit trail on TaskManager.API AuditController:
 *   GET  /api/Audit/Activity           — query with filters
 *   GET  /api/Audit/Activity/Export    — CSV download
 *
 * Both endpoints are Admin-role gated server-side.
 *
 * Contract deliberately built against AuditController.cs (NOT the user-app
 * activityLogApi.ts, which was wrong — see US-16-GR-24). Query params:
 *   dateFrom, dateTo, actorType, category, actorId, aiActionsOnly, limit, offset
 * Response rows are serialised ActivityLogEntity. Enums have no
 * JsonStringEnumConverter so they arrive/are sent as INTEGERS.
 */

import { authenticatedFetch } from '../api/authenticatedFetch';
import { COLDSTART_RETRY_CONFIG } from '../api/resilientFetch';
import { triggerCsvBlobDownload } from '../utils/downloadCsv';

// ---------------------------------------------------------------------------
// Enums — numeric, mirror TaskManager.Models.Enums.ActivityLogEnums
// ---------------------------------------------------------------------------

export enum ActorType {
  User = 0,
  AIAgent = 1,
  System = 2,
}

export enum ActivityCategory {
  Task = 0,
  Board = 1,
  Membership = 2,
  AIAction = 3,
  AIAutonomous = 4,
  Outsourcing = 5,
  Funding = 6,
  Admin = 7,
  Auth = 8,
}

export const ACTOR_TYPE_LABELS: Record<ActorType, string> = {
  [ActorType.User]: 'User',
  [ActorType.AIAgent]: 'AI Agent',
  [ActorType.System]: 'System',
};

export const ACTIVITY_CATEGORY_LABELS: Record<ActivityCategory, string> = {
  [ActivityCategory.Task]: 'Task',
  [ActivityCategory.Board]: 'Board',
  [ActivityCategory.Membership]: 'Membership',
  [ActivityCategory.AIAction]: 'AI Action',
  [ActivityCategory.AIAutonomous]: 'AI Autonomous',
  [ActivityCategory.Outsourcing]: 'Outsourcing',
  [ActivityCategory.Funding]: 'Funding',
  [ActivityCategory.Admin]: 'Admin',
  [ActivityCategory.Auth]: 'Auth',
};

// ---------------------------------------------------------------------------
// DTO — serialised ActivityLogEntity
// ---------------------------------------------------------------------------

export interface ActivityLogEntry {
  id: string;
  timestamp: string;
  actorType: ActorType;
  actorId: string | null;
  category: ActivityCategory;
  action: string;
  originalInputHash: string | null;
  decisionSummary: string | null;
  confidenceScore: number | null;
  modelUsed: string | null;
  promptTokens: number | null;
  completionTokens: number | null;
  outputHash: string | null;
  relatedEntityId: string | null;
  compensatesActivityLogId: string | null;
}

export interface ActivityLogFilter {
  /** ISO-8601 (or yyyy-MM-dd) inclusive lower bound. */
  dateFrom?: string;
  /** ISO-8601 (or yyyy-MM-dd) inclusive upper bound. */
  dateTo?: string;
  actorType?: ActorType;
  category?: ActivityCategory;
  actorId?: string;
  aiActionsOnly?: boolean;
  limit?: number;
  offset?: number;
}

const BASE = '/api/Audit/Activity';

function buildQuery(filter: ActivityLogFilter): string {
  const params = new URLSearchParams();
  if (filter.dateFrom) params.set('dateFrom', filter.dateFrom);
  if (filter.dateTo) params.set('dateTo', filter.dateTo);
  if (filter.actorType !== undefined) params.set('actorType', String(filter.actorType));
  if (filter.category !== undefined) params.set('category', String(filter.category));
  if (filter.actorId) params.set('actorId', filter.actorId);
  if (filter.aiActionsOnly) params.set('aiActionsOnly', 'true');
  if (filter.limit !== undefined) params.set('limit', String(filter.limit));
  if (filter.offset !== undefined) params.set('offset', String(filter.offset));
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/** GET /api/Audit/Activity — filtered Receipt query. */
export async function queryActivityLog(
  filter: ActivityLogFilter = {},
): Promise<ActivityLogEntry[]> {
  const response = await authenticatedFetch(`${BASE}${buildQuery(filter)}`, undefined, { retryConfig: COLDSTART_RETRY_CONFIG });
  if (!response.ok) {
    throw new Error(`Audit query failed: HTTP ${response.status} ${response.statusText}`);
  }
  return response.json() as Promise<ActivityLogEntry[]>;
}

/**
 * GET /api/Audit/Activity/Export — download the filtered Receipt trail as CSV.
 * Triggers a browser download of the returned file.
 */
export async function exportActivityLogCsv(filter: ActivityLogFilter = {}): Promise<void> {
  const response = await authenticatedFetch(`${BASE}/Export${buildQuery(filter)}`, undefined, { retryConfig: COLDSTART_RETRY_CONFIG });
  if (!response.ok) {
    throw new Error(`Audit export failed: HTTP ${response.status} ${response.statusText}`);
  }
  const blob = await response.blob();
  const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '');
  triggerCsvBlobDownload(`activity-log-${stamp}.csv`, blob);
}
